import { PropsWithChildren, Children } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

const FIXED_MOBILE_PADDING = 32;
const FIXED_PADDING = 60;
const MAX_CONTENT_WIDTH = 1152;

// DefaultStyles depends on the order of keys in revisedLayoutCounts.
// Any future layouts should be added to the end, not the beginning.
const revisedLayoutCounts: { [key: string]: number } = {
    "12": 1,
    "6-6": 2,
    "4-4-4": 3,
    "3-3-3-3": 4,
    "2-2-2-2-2-2": 6,
    "4-8": 2,
    "7-5": 2,
    "3-9": 2,
    "8-4": 2,
    "9-3": 2,
    "3-3-6": 3,
    "6-3-3": 3,
    "8": 1,
    "10": 1
};

type Entries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];

const getEntries = <T extends object>(obj: T) =>
    Object.entries(obj) as Entries<T>;

const DefaultStyles = Object.fromEntries(
    getEntries(revisedLayoutCounts)
        .slice(0, 5)
        .map(([key, val]) => [val, key])
);

Object.freeze(DefaultStyles);

export type layoutRevised = keyof typeof revisedLayoutCounts;

type AlignTypes = "default" | "top" | "bottom" | "center";

export interface RowRevisedProps {
    layout?: layoutRevised;
    alignItems?: AlignTypes;
    adjustedMobilePadding?: number;
    adjustedPadding?: number;
    adjustedMaxContentWidth?: number;
}

// eslint-disable-next-line complexity
export const RowRevised = (props: PropsWithChildren<RowRevisedProps>) => {
    let { layout } = props;
    const { alignItems } = props;

    const { adjustedMobilePadding } = props;
    const { adjustedPadding } = props;
    const { adjustedMaxContentWidth } = props;

    // null and undefined checks to accomodate when padding = 0
    const mobilePadding =
        adjustedMobilePadding !== null && adjustedMobilePadding !== undefined
            ? adjustedMobilePadding
            : FIXED_MOBILE_PADDING;
    const padding =
        adjustedPadding !== null && adjustedPadding !== undefined
            ? adjustedPadding
            : FIXED_PADDING;
    const maxContentWidth = adjustedMaxContentWidth
        ? adjustedMaxContentWidth
        : MAX_CONTENT_WIDTH;

    const children = Children.toArray(props.children);
    if (layout === undefined) {
        layout = DefaultStyles[children.length];
        if (layout === "") {
            throw new Error(
                `${children.length} is an invalid number of children`
            );
        }
    } else {
        // @ts-ignore
        if (children.length != revisedLayoutCounts[layout]) {
            throw new Error(
                // @ts-ignore
                `Row with layout "${layout}" should have ${revisedLayoutCounts[layout]} children`
            );
        }
    }
    const alignItemStyles = {
        ...(alignItems === "default" && {
            alignItems: "stretch"
        }),
        ...(alignItems === "top" && { alignItems: "start" }),
        ...(alignItems === "bottom" && { alignItems: "end" }),
        ...(alignItems === "center" && {
            alignItems: "center"
        }),
        ...(!alignItems && { alignItems: "stretch" })
    };
    const commonLayoutStyles = {
        display: "grid",
        justifyContent: "space-between",
        margin: "auto",
        maxWidth: "100%",
        boxSizing: "border-box",
        paddingInlineStart: `${mobilePadding}px`,
        paddingInlineEnd: `${mobilePadding}px`,
        gap: "24px",

        ["& + .row-layout"]: {
            marginBlockStart: "24px",

            [media.above.sm]: {
                marginBlockStart: "32px"
            }
        }
    };

    return (
        <div
            className={`row-layout`}
            css={[
                layout === "12" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr)",
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "24px",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns": "minmax(0, 1fr)",
                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns": "minmax(0, 1fr)",
                            "gap": "32px",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "6-6" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr)",
                        ".row-layout": {
                            padding: "0"
                        },
                        [media.only.sm]: {
                            display: "grid",
                            justifyContent: "space-between",
                            margin: "auto",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                            paddingInlineStart: `${padding}px`,
                            paddingInlineEnd: `${padding}px`,
                            gridTemplateColumns:
                                "minmax(0, 1fr) minmax(0, 1fr)",
                            gap: "24px"
                        },
                        [media.above.md]: {
                            display: "grid",
                            justifyContent: "space-between",
                            margin: "auto",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                            paddingInlineStart: `${padding}px`,
                            paddingInlineEnd: `${padding}px`,
                            width: `${maxContentWidth + padding * 2}px`,
                            gridTemplateColumns:
                                "minmax(0, 1fr) minmax(0, 1fr)",
                            gap: "32px"
                        }
                    }),
                layout === "4-4-4" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr)",
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "24px",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns": "repeat(3, minmax(0, 1fr))",
                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns": "repeat(3, minmax(0, 1fr))",
                            "gap": "32px",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "3-3-3-3" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr)",
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "24px",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns":
                                "minmax(0 1fr) minmax(0, 1fr)",
                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns": "repeat(4, minmax(0, 1fr))",
                            "gap": "32px",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "2-2-2-2-2-2" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 1fr)",
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "24px",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns": "repeat(2, minmax(0, 1fr))",

                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns": "repeat(6, minmax(0, 1fr))",
                            "gap": "32px",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "4-8" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr)",
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns":
                                "minmax(0px, 0.3333fr) minmax(0px, 0.6666fr)",
                            "gap": "24px",
                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns":
                                "minmax(0px, 0.3333fr) minmax(0px, 0.6666fr)",
                            "gap": "32px",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "7-5" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr)",
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "24px",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns":
                                "minmax(0px, .5833fr) minmax(0px, 0.4166fr)",
                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns":
                                "minmax(0px, .5833fr) minmax(0px, 0.4166fr)",
                            "gap": "32px",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "3-9" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr)",
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "24px",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns": "minmax(0, 1fr)",
                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns":
                                "minmax(0px, .25fr) minmax(0px, .75fr)",
                            "gap": "32px",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "8-4" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr)",
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "24px",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns":
                                "minmax(0px, .6666fr) minmax(0px, 0.3333fr)",
                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns":
                                "minmax(0px, .6666fr) minmax(0px, 0.3333fr)",
                            "gap": "32px",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "9-3" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr)",
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "24px",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns": "minmax(0, 1fr)",

                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns":
                                "minmax(0px, 0.75fr) minmax(0px, .25fr)",
                            "gap": "32px",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "3-3-6" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 1fr)",
                        "gridTemplateRows": "minmax(0, 1fr) minmax(0, auto)",
                        "> *:nth-of-type(3)": {
                            gridArea: "2 / 1 / 3 / 3"
                        },
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "24px",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns": "repeat(2, minmax(0, 1fr))",
                            "gridTemplateRows":
                                "minmax(0, 1fr) minmax(0, auto)",
                            "> *:nth-of-type(3)": {
                                gridArea: "2 / 1 / 3 / 3"
                            },

                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns":
                                "minmax(0px, 1fr) minmax(0px, 1fr) minmax(0px, 2fr)",
                            "gridTemplateRows": "auto",
                            "> *:nth-of-type(3)": {
                                gridArea: "auto"
                            },
                            "gap": "32px",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "6-3-3" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 1fr)",
                        "gridTemplateRows": "minmax(0, auto) minmax(0, 1fr)",
                        "> *:nth-of-type(1)": {
                            gridArea: "1 / 1 / 1 / 3"
                        },
                        "> *:nth-of-type(2)": {
                            gridArea: "2 / 1 / 3 / 2"
                        },
                        "> *:nth-of-type(3)": {
                            gridArea: "2 / 2 / 3 / 3"
                        },
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "24px",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns": "repeat(2, minmax(0, 1fr))",
                            "gridTemplateRows":
                                "minmax(0, auto) minmax(0, 1fr)",
                            "> *:nth-of-type(1)": {
                                gridArea: "1 / 1 / 1 / 3"
                            },
                            "> *:nth-of-type(2)": {
                                gridArea: "2 / 1 / 3 / 2"
                            },
                            "> *:nth-of-type(3)": {
                                gridArea: "2 / 2 / 3 / 3"
                            },

                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns":
                                "minmax(0px, 2fr) minmax(0px, 1fr) minmax(0px, 1fr)",
                            "gridTemplateRows": "auto",
                            "> *:nth-of-type(1)": {
                                gridArea: "auto"
                            },
                            "> *:nth-of-type(2)": {
                                gridArea: "auto"
                            },
                            "> *:nth-of-type(3)": {
                                gridArea: "auto"
                            },
                            "gap": "32px",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "8" &&
                    css({
                        ...alignItemStyles,
                        "display": "grid",
                        "justifyContent": "space-between",
                        "margin": "auto",
                        "maxWidth": "100%",
                        "boxSizing": "border-box",
                        "gap": "0",
                        "paddingInlineStart": `${mobilePadding}px`,
                        "paddingInlineEnd": `${mobilePadding}px`,
                        "gridTemplateColumns": "minmax(0, 1fr)",
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "0",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns":
                                "minmax(0, 1fr) minmax(0, 10fr) minmax(0, 1fr)",
                            "> *:nth-of-type(1)": {
                                gridColumnStart: "2"
                            },
                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns":
                                "minmax(0, 2fr)  minmax(0, 8fr) minmax(0, 2fr)",
                            "> *:nth-of-type(1)": {
                                gridColumnStart: "2"
                            },
                            "gap": "0",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    }),
                layout === "10" &&
                    css({
                        ...alignItemStyles,
                        "display": "grid",
                        "justifyContent": "space-between",
                        "margin": "auto",
                        "maxWidth": "100%",
                        "boxSizing": "border-box",
                        "gap": "0",
                        "paddingInlineStart": `${mobilePadding}px`,
                        "paddingInlineEnd": `${mobilePadding}px`,

                        "gridTemplateColumns": "minmax(0, 1fr)",
                        ".row-layout": {
                            marginBlockStart: "24px"
                        },
                        [media.only.sm]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "gap": "0",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "gridTemplateColumns": "minmax(0, 1fr)",
                            ".row-layout": {
                                padding: 0
                            }
                        },
                        [media.above.md]: {
                            "display": "grid",
                            "justifyContent": "space-between",
                            "margin": "auto",
                            "maxWidth": "100%",
                            "boxSizing": "border-box",
                            "paddingInlineStart": `${padding}px`,
                            "paddingInlineEnd": `${padding}px`,
                            "width": `${maxContentWidth + padding * 2}px`,
                            "gridTemplateColumns":
                                "minmax(0, 1fr)  minmax(0, 10fr) minmax(0, 1fr)",
                            "> *:nth-of-type(1)": {
                                gridColumnStart: "2"
                            },
                            "gap": "0",
                            ".row-layout": {
                                padding: 0
                            }
                        }
                    })
            ]}
        >
            {children.map((child) => child)}
        </div>
    );
};
